import { HttpErrorResponse } from '@angular/common/http';

/**
 * Error Handler Utility
 * Provides centralized error message extraction from HTTP responses
 */
export class ErrorHandler {
  /**
   * Extract user-friendly error message from HttpErrorResponse
   * Handles various error response formats from the API
   * @param error HttpErrorResponse object
   * @returns User-friendly error message string
   */
  static extractErrorMessage(error: HttpErrorResponse): string {
    if (!error) {
      return 'An unexpected error occurred.';
    }

    // Handle network errors
    if (error.status === 0) {
      return 'Network error. Please check your internet connection.';
    }

    // Check if error.error exists
    if (!error.error) {
      return error.message || 'An unexpected error occurred.';
    }

    const errorBody = error.error;

    // Handle validation errors (400 with errors array)
    if (error.status === 400 && errorBody.errors && Array.isArray(errorBody.errors)) {
      const validationMessages = errorBody.errors
        .map((err: any) => {
          if (err.field && err.message) {
            return `${err.field}: ${err.message}`;
          }
          return err.message || err.toString();
        })
        .filter((msg: string) => msg && msg.trim().length > 0)
        .join('\n');

      if (validationMessages) {
        return validationMessages;
      }
    }

    // Handle ModelState errors from .NET API (common format)
    if (errorBody.errors && typeof errorBody.errors === 'object') {
      const modelStateErrors: string[] = [];
      Object.keys(errorBody.errors).forEach(key => {
        const errors = errorBody.errors[key];
        if (Array.isArray(errors)) {
          errors.forEach(msg => modelStateErrors.push(`${key}: ${msg}`));
        } else if (typeof errors === 'string') {
          modelStateErrors.push(`${key}: ${errors}`);
        }
      });

      if (modelStateErrors.length > 0) {
        return modelStateErrors.join('\n');
      }
    }

    // Handle error with message property
    if (errorBody.message && typeof errorBody.message === 'string') {
      return errorBody.message;
    }

    // Handle error with title property
    if (errorBody.title && typeof errorBody.title === 'string') {
      return errorBody.title;
    }

    // Handle error with detail property
    if (errorBody.detail && typeof errorBody.detail === 'string') {
      return errorBody.detail;
    }

    // Handle string error
    if (typeof errorBody === 'string') {
      return errorBody;
    }

    // Handle specific status codes
    switch (error.status) {
      case 400:
        return 'Bad request. Please check your input.';
      case 401:
        return 'Unauthorized. Please log in again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'Conflict. The resource already exists or there is a conflict with the current state.';
      case 422:
        return 'Unprocessable entity. Please check your input.';
      case 500:
        return 'Server error occurred. Please try again later or contact support.';
      case 502:
        return 'Bad gateway. The server is temporarily unavailable.';
      case 503:
        return 'Service unavailable. Please try again later.';
      case 504:
        return 'Gateway timeout. The request took too long to process.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  }

  /**
   * Check if the error is a client error (4xx)
   * @param error HttpErrorResponse object
   * @returns true if status code is between 400-499
   */
  static isClientError(error: HttpErrorResponse): boolean {
    return error.status >= 400 && error.status < 500;
  }

  /**
   * Check if the error is a server error (5xx)
   * @param error HttpErrorResponse object
   * @returns true if status code is between 500-599
   */
  static isServerError(error: HttpErrorResponse): boolean {
    return error.status >= 500 && error.status < 600;
  }

  /**
   * Get appropriate status code for toast service
   * @param error HttpErrorResponse object
   * @returns HTTP status code
   */
  static getStatusCode(error: HttpErrorResponse): number {
    return error.status || 0;
  }
}
